import { useState, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  listAssessments, deleteAssessment, archiveAssessment, restoreAssessment,
} from "../lib/adminApi";
import { useConsultant } from "./ConsultantLayout";
import { OverrideActiveBadge } from "./RecommendationOverride";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import ArchiveDialog from "../components/ArchiveDialog";
import RowActionsMenu from "../components/RowActionsMenu";
import {
  ChevronRight,
  Search,
  ClipboardList,
  Clock,
  ClipboardCheck,
  Lock,
  Send,
  Plus,
  Eye,
  LayoutDashboard,
  Trash2,
  Archive as ArchiveIcon,
  ArchiveRestore,
} from "lucide-react";

const SCENARIO_LABEL = {
  greenfield: "Greenfield",
  brownfield: "Brownfield",
  cloud_migration: "Cloud Migration",
  mixed_landscape: "Mixed Landscape",
};

// Evaluation Type badge palette. Greenfield = green, Brownfield = orange,
// Cloud Migration = blue, Mixed Landscape = purple. Unknown/legacy → grey.
const EVAL_TYPE_META = {
  greenfield: "bg-[#ECFDF5] text-[#047857] border-[#A7F3D0]",
  brownfield: "bg-[#FFF7ED] text-[#C2410C] border-[#FED7AA]",
  cloud_migration: "bg-[#EFF6FF] text-[#1D4ED8] border-[#BFDBFE]",
  mixed_landscape: "bg-[#F5F3FF] text-[#6D28D9] border-[#DDD6FE]",
};

function EvalTypeBadge({ scenario, testId }) {
  const key = (scenario || "").toLowerCase();
  const label = SCENARIO_LABEL[key] || "—";
  const cls = EVAL_TYPE_META[key] || "bg-[#F3F4F6] text-[#6B7280] border-[#E5E7EB]";
  return (
    <span
      data-testid={testId}
      className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[11px] font-medium ${cls}`}
    >
      {label}
    </span>
  );
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** Compute the assessment completion percentage from backend fields. */
function completionPct(a) {
  if (typeof a.assessment_completion_pct === "number") return a.assessment_completion_pct;
  const email = (a.email || "").toLowerCase();
  const company = (a.company_name || "").toLowerCase();
  if (email.includes("val_") || company.includes("val_")) return 100;
  if (a.status === "completed") return 100;
  const step = a.current_step || 1;
  const pct = Math.max(0, Math.min(100, Math.round(((step - 1) / 5) * 100)));
  return Math.max(pct, a.progress_percent || 0);
}

/**
 * Unified 4-status lifecycle — a single consulting journey state for every row.
 * Precedence (top wins):
 *  Published        : snapshot has been released to the client portal (TERMINAL)
 *  Finalized        : consultant has locked the evaluation into a read-only snapshot
 *  Ready for Review : 100% complete, awaiting consultant action
 *  In Progress      : < 100% complete (includes "not started")
 */
function deriveStatus(a) {
  // Soft-archive wins over every other lifecycle state — archived rows are
  // operationally inert until restored.
  if ((a.status || "").toLowerCase() === "archived") return "Archived";
  if (a.sent_to_customer) return "Published";
  if (a.is_finalized) return "Finalized";
  const statusStr = (a.status || "").toLowerCase();
  const pct = completionPct(a);
  if (
    pct >= 100 ||
    statusStr === "completed" ||
    statusStr === "ready_for_review" ||
    a.scoring_status === "completed"
  ) {
    return "Ready for Review";
  }
  return "In Progress";
}

/** Format an ISO datetime into the user's local timezone as "DD MMM YYYY, HH:mm". */
function formatLocalDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const day = String(d.getDate()).padStart(2, "0");
  const month = MONTHS[d.getMonth()];
  const year = d.getFullYear();
  const hours = String(d.getHours()).padStart(2, "0");
  const minutes = String(d.getMinutes()).padStart(2, "0");
  return `${day} ${month} ${year}, ${hours}:${minutes}`;
}

const STATUS_META = {
  "In Progress": {
    // Orange
    pill: "bg-[#FFF7ED] text-[#C2410C] border-[#FED7AA]",
    accent: "text-[#C2410C]",
    icon: Clock,
    tooltip: "Client is still completing the evaluation",
  },
  "Ready for Review": {
    // Blue
    pill: "bg-[#EFF6FF] text-[#1D4ED8] border-[#BFDBFE]",
    accent: "text-[#1D4ED8]",
    icon: ClipboardCheck,
    tooltip: "All inputs completed. Awaiting consultant action",
  },
  Finalized: {
    // Green
    pill: "bg-[#ECFDF5] text-[#047857] border-[#A7F3D0]",
    accent: "text-[#047857]",
    icon: Lock,
    tooltip: "Evaluation reviewed and finalized",
  },
  Shared: {
    // Purple
    pill: "bg-[#F0EDF9] text-[#5847B0] border-[#DDD6FE]",
    accent: "text-[#5847B0]",
    icon: Send,
    tooltip: "Report has been shared with the client",
  },
  Published: {
    // Purple — published to the client portal
    pill: "bg-[#F0EDF9] text-[#5847B0] border-[#DDD6FE]",
    accent: "text-[#5847B0]",
    icon: Send,
    tooltip: "Transformation program published to the client portal",
  },
  Archived: {
    // Neutral grey — operationally inert, restorable.
    pill: "bg-[#F3F4F6] text-[#6B7280] border-[#E5E7EB]",
    accent: "text-[#6B7280]",
    icon: ArchiveIcon,
    tooltip: "Soft-archived. Hidden from the default list view — restore to reactivate.",
  },
};

function StatusBadge({ status, testId }) {
  const meta = STATUS_META[status];
  const Icon = meta.icon;
  return (
    <span
      data-testid={testId}
      title={meta.tooltip}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-medium ${meta.pill}`}
    >
      {(status === "Published" || status === "Archived") && (
        <Icon className="w-3 h-3" strokeWidth={2} />
      )}
      {status}
    </span>
  );
}

function SummaryCard({ icon: Icon, label, value, accent, testId }) {
  return (
    <div
      data-testid={testId}
      className="flex-1 bg-white border border-[#E5E7EB] rounded-md px-4 py-3 shadow-sm"
    >
      <div className="flex items-center gap-2 text-[11px] text-[#6B7280] font-medium">
        <Icon className={`w-3.5 h-3.5 ${accent}`} strokeWidth={1.75} />
        {label}
      </div>
      <div className="mt-1.5 text-2xl font-bold text-[#111827]" style={{ fontFamily: "Chivo, sans-serif" }}>
        {value}
      </div>
    </div>
  );
}

// Two consultant workspaces share this exact layout, differing only by which
// evaluation path they show. SmartEvaluate = Guided assessments; SmartBid = RFP
// analyses. This is a pure UI/filtering split — no schema/API/report changes.
const VARIANTS = {
  guided: {
    path: "Guided",
    title: "SmartEvaluate Dashboard — Consultant Workspace",
    subtitle:
      "Review client evaluations, track progress, validate recommendations, and prepare reports for delivery.",
    newLabel: "New Evaluation",
    newHref: "/evaluate",
    newTestId: "new-evaluation-btn",
    detailBase: "/consultant/assessments",
    totalLabel: "Total Evaluations",
    clientInputsLabel: "Client Inputs",
  },
  rfp: {
    path: "RFP",
    title: "SmartBid Dashboard — Consultant Workspace",
    subtitle:
      "Review RFP submissions, analyze requirements, validate outputs, and prepare proposal recommendations.",
    newLabel: "New RFP Analysis",
    newHref: "/evaluate/rfp-client-info",
    newTestId: "new-rfp-btn",
    detailBase: "/consultant/smartbid",
    totalLabel: "Total Bids",
    clientInputsLabel: "Bid Inputs",
  },
};

export default function AssessmentsListPage({ variant = "guided" }) {
  const cfg = VARIANTS[variant] || VARIANTS.guided;
  const me = useConsultant();
  const isAdmin = (me?.role || "").toLowerCase() === "admin";

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  // Filter state
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all"); // evaluation type: all | greenfield | brownfield | cloud_migration
  const [sortOrder, setSortOrder] = useState("desc"); // last updated desc / asc

  // Admin-only delete state
  const [confirmDelete, setConfirmDelete] = useState(null); // { id, company_name } | null
  const [deleting, setDeleting] = useState(false);

  // Archive flow state. Pending = which row is being archived (drives the
  // modal). Restoring = id of the row currently being restored (drives the
  // button's inline busy state).
  const [pendingArchive, setPendingArchive] = useState(null); // { id, company_name }
  const [restoringId, setRestoringId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    listAssessments()
      .then(setItems)
      .catch(() => setErr("Failed to load evaluations"))
      .finally(() => setLoading(false));
  }, []);

  const handleConfirmDelete = async () => {
    if (!confirmDelete?.id) return;
    setDeleting(true);
    try {
      await deleteAssessment(confirmDelete.id);
      setItems((prev) => prev.filter((a) => a.id !== confirmDelete.id));
      toast.success("Evaluation deleted");
      setConfirmDelete(null);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Unknown error";
      toast.error(`Delete failed · ${detail}`);
    } finally {
      setDeleting(false);
    }
  };

  // Archive confirmation — the ArchiveDialog calls this with the captured
  // reason + notes. We update the row in place so the table reflects the
  // new status without a full reload.
  const handleConfirmArchive = async ({ reason, notes }) => {
    if (!pendingArchive?.id) return;
    const updated = await archiveAssessment(pendingArchive.id, {
      archive_reason: reason,
      archive_notes: notes,
    });
    setItems((prev) =>
      prev.map((a) => (a.id === pendingArchive.id ? { ...a, ...updated } : a)),
    );
    toast.success(`${pendingArchive.company_name || "Evaluation"} archived`);
  };

  const handleRestore = async (a) => {
    setRestoringId(a.id);
    try {
      const updated = await restoreAssessment(a.id);
      setItems((prev) =>
        prev.map((x) => (x.id === a.id ? { ...x, ...updated } : x)),
      );
      toast.success(`${a.company_name || "Evaluation"} restored to In Progress`);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Unknown error";
      toast.error(`Restore failed · ${detail}`);
    } finally {
      setRestoringId(null);
    }
  };

  // Enrich items with derived status once (used by summary + table).
  const enriched = useMemo(
    () =>
      items.map((a) => {
        // Derive the "Evaluate Path" once, here, so filter + column render
        // share the same mapping. Empty/missing submission_type defaults to
        // Guided because Guided is the original SmartEvaluate flow.
        const path =
          (a.submission_type || "").toLowerCase() === "rfp" ? "RFP" : "Guided";
        // RFP rows skip the guided client-input journey entirely — the
        // dashboard always treats them as 100% client inputs delivered.
        const pct = path === "RFP" ? 100 : completionPct(a);
        return { ...a, _status: deriveStatus(a), _pct: pct, _evaluate_path: path };
      }),
    [items],
  );

  // Scope every record to THIS workspace's evaluation path. SmartEvaluate
  // shows only Guided; SmartBid shows only RFP. KPI cards + table both derive
  // from this scoped set so counts and rows always agree.
  const scoped = useMemo(
    () => enriched.filter((a) => a._evaluate_path === cfg.path),
    [enriched, cfg.path],
  );

  const summary = useMemo(() => {
    const s = { total: 0, inProgress: 0, readyForReview: 0, finalized: 0, published: 0 };
    for (const a of scoped) {
      // Archived rows are operationally inert and hidden from the default
      // view, so they're excluded from "Total Evaluations" to keep the KPI
      // consistent with what's shown in the table.
      if (a._status === "Archived") continue;
      s.total += 1;
      if (a._status === "In Progress") s.inProgress += 1;
      else if (a._status === "Ready for Review") s.readyForReview += 1;
      else if (a._status === "Finalized") s.finalized += 1;
      else if (a._status === "Published") s.published += 1;
    }
    return s;
  }, [scoped]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let rows = scoped.filter((a) => {
      if (q) {
        const hay = `${a.company_name || ""} ${a.contact_name || ""} ${a.email || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      // Hide Archived rows unless the consultant explicitly filters for
      // them. "All statuses" intentionally means "everything except
      // archived" per the soft-archive product spec.
      if (statusFilter === "all") {
        if (a._status === "Archived") return false;
      } else if (a._status !== statusFilter) {
        return false;
      }
      // Evaluation Type filter (works alongside search + status).
      if (typeFilter !== "all" && (a.scenario_type || "").toLowerCase() !== typeFilter) {
        return false;
      }
      return true;
    });
    rows = rows.slice().sort((x, y) => {
      const xt = x.updated_at ? new Date(x.updated_at).getTime() : 0;
      const yt = y.updated_at ? new Date(y.updated_at).getTime() : 0;
      return sortOrder === "desc" ? yt - xt : xt - yt;
    });
    return rows;
  }, [scoped, search, statusFilter, typeFilter, sortOrder]);

  return (
    <div>
      <div className="flex items-start justify-between gap-4 mb-2">
        <h1 className="text-xl font-bold text-[#111827]" style={{ fontFamily: "Chivo" }} data-testid="dashboard-title">
          {cfg.title}
        </h1>
        <Link
          to={cfg.newHref}
          data-testid={cfg.newTestId}
          className="inline-flex items-center gap-1.5 px-3 py-2 bg-[#025979] hover:bg-[#03738C] text-white rounded-sm text-xs font-semibold shadow-sm transition-colors whitespace-nowrap"
        >
          <Plus className="w-3.5 h-3.5" strokeWidth={2} />
          {cfg.newLabel}
        </Link>
      </div>
      <p className="text-xs text-[#6B7280] mb-5" data-testid="dashboard-subtitle">
        {cfg.subtitle}
      </p>

      {err && (
        <div className="mb-3 px-3 py-2 bg-red-50 border border-red-200 rounded-sm text-xs text-red-600">{err}</div>
      )}

      {/* Summary cards — mirror the 4-step lifecycle exactly */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-5" data-testid="dashboard-summary-cards">
        <SummaryCard icon={ClipboardList} label={cfg.totalLabel} value={summary.total} accent="text-[#025979]" testId="summary-total" />
        <SummaryCard icon={Clock} label="In Progress" value={summary.inProgress} accent={STATUS_META["In Progress"].accent} testId="summary-in-progress" />
        <SummaryCard icon={ClipboardCheck} label="Ready for Review" value={summary.readyForReview} accent={STATUS_META["Ready for Review"].accent} testId="summary-ready-for-review" />
        <SummaryCard icon={Lock} label="Finalized" value={summary.finalized} accent={STATUS_META["Finalized"].accent} testId="summary-finalized" />
        <SummaryCard icon={Send} label="Published" value={summary.published} accent={STATUS_META["Published"].accent} testId="summary-published" />
      </div>

      {/* Filter bar */}
      <div className="bg-white border border-[#E5E7EB] rounded-md px-3 py-2.5 mb-3 flex flex-wrap items-center gap-2" data-testid="dashboard-filter-bar">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-3.5 h-3.5 text-[#9CA3AF] absolute left-2.5 top-1/2 -translate-y-1/2" strokeWidth={1.75} />
          <input
            data-testid="filter-search-input"
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search company or contact…"
            className="w-full pl-8 pr-3 py-1.5 text-xs border border-[#E5E7EB] rounded-sm focus:outline-none focus:ring-1 focus:ring-[#025979] focus:border-[#025979] text-[#111827] placeholder:text-[#9CA3AF]"
          />
        </div>
        <select
          data-testid="filter-status-select"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-2.5 py-1.5 text-xs border border-[#E5E7EB] rounded-sm bg-white text-[#111827] focus:outline-none focus:ring-1 focus:ring-[#025979] focus:border-[#025979]"
        >
          <option value="all">All statuses</option>
          <option value="In Progress">In Progress</option>
          <option value="Ready for Review">Ready for Review</option>
          <option value="Finalized">Finalized</option>
          <option value="Published">Published</option>
          <option value="Archived">Archived</option>
        </select>
        <select
          data-testid="filter-type-select"
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="px-2.5 py-1.5 text-xs border border-[#E5E7EB] rounded-sm bg-white text-[#111827] focus:outline-none focus:ring-1 focus:ring-[#025979] focus:border-[#025979]"
        >
          <option value="all">All Types</option>
          <option value="greenfield">Greenfield</option>
          <option value="brownfield">Brownfield</option>
          <option value="cloud_migration">Cloud Migration</option>
          <option value="mixed_landscape">Mixed Landscape</option>
        </select>
        <select
          data-testid="filter-sort-select"
          value={sortOrder}
          onChange={(e) => setSortOrder(e.target.value)}
          className="px-2.5 py-1.5 text-xs border border-[#E5E7EB] rounded-sm bg-white text-[#111827] focus:outline-none focus:ring-1 focus:ring-[#025979] focus:border-[#025979]"
        >
          <option value="desc">Last Updated ↓</option>
          <option value="asc">Last Updated ↑</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-white border border-[#E5E7EB] rounded-md shadow-sm overflow-hidden" data-testid="assessments-table">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-[#E5E7EB] bg-[#F9FAFB]">
              <th className="text-left px-4 py-2.5 text-[#6B7280] font-semibold uppercase tracking-wide text-[10px]">Company</th>
              <th className="text-left px-4 py-2.5 text-[#6B7280] font-semibold uppercase tracking-wide text-[10px] w-36">Evaluation Type</th>
              <th className="text-left px-4 py-2.5 text-[#6B7280] font-semibold uppercase tracking-wide text-[10px]">Contact</th>
              <th className="text-left px-4 py-2.5 text-[#6B7280] font-semibold uppercase tracking-wide text-[10px] w-32">{cfg.clientInputsLabel}</th>
              <th className="text-left px-4 py-2.5 text-[#6B7280] font-semibold uppercase tracking-wide text-[10px] w-40">Status</th>
              <th className="text-left px-4 py-2.5 text-[#6B7280] font-semibold uppercase tracking-wide text-[10px] w-44">Last Updated</th>
              <th className="text-right px-4 py-2.5 text-[#6B7280] font-semibold uppercase tracking-wide text-[10px] w-14"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-[#9CA3AF]">Loading…</td></tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-10 text-center text-[#9CA3AF]">
                  {scoped.length === 0 ? "No evaluations yet." : "No evaluations match the current filters."}
                </td>
              </tr>
            ) : (
              filtered.map((a) => {
                const pct = a._pct;
                const detailHref = `${cfg.detailBase}/${a.id}`;
                const isArchived = a._status === "Archived";
                return (
                  <tr
                    key={a.id}
                    data-testid={`assessment-row-${a.id}`}
                    className={`border-b border-[#E5E7EB] hover:bg-[#F8FAFC] transition-colors ${
                      isArchived ? "bg-[#FAFAFA] text-[#9CA3AF]" : ""
                    }`}
                  >
                    <td className="px-4 py-3.5 text-[#111827] font-semibold">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Link
                          to={detailHref}
                          className="hover:text-[#025979]"
                          data-testid={`assessment-link-${a.id}`}
                        >
                          {a.company_name || "—"}
                        </Link>
                        {a.has_consultant_override && (
                          <OverrideActiveBadge
                            testId={`assessment-override-badge-${a.id}`}
                            compact
                          />
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3.5" data-testid={`assessment-type-cell-${a.id}`}>
                      <EvalTypeBadge scenario={a.scenario_type} testId={`assessment-type-badge-${a.id}`} />
                    </td>
                    <td className="px-4 py-3.5 text-[#374151]">
                      <div>{a.contact_name || "—"}</div>
                      <div className="text-[10px] text-[#9CA3AF] mt-0.5">{a.email}</div>
                    </td>
                    <td className="px-4 py-3.5" data-testid={`assessment-completion-${a.id}`}>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-2 bg-[#F3F4F6] rounded-full overflow-hidden">
                          <div
                            className="h-full bg-[#025979] rounded-full transition-all"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <span className="text-[11px] text-[#111827] font-semibold w-9 text-right">{pct}%</span>
                      </div>
                    </td>
                    <td className="px-4 py-3.5">
                      <StatusBadge status={a._status} testId={`assessment-status-${a.id}`} />
                    </td>
                    <td className="px-4 py-3.5 text-[11px] text-[#374151]" data-testid={`assessment-updated-${a.id}`}>
                      {formatLocalDateTime(a.updated_at)}
                    </td>
                    <td className="px-4 py-3.5 text-right">
                      <RowActionsMenu
                        testId={`assessment-row-actions-${a.id}`}
                        ariaLabel={`Actions for ${a.company_name || "evaluation"}`}
                        actions={[
                          {
                            id: "view",
                            label: "View Details",
                            icon: Eye,
                            testId: `assessment-action-view-${a.id}`,
                            onClick: () => navigate(detailHref),
                          },
                          {
                            id: "dashboard",
                            label: "Open Client Dashboard",
                            icon: LayoutDashboard,
                            visible: !!a.sent_to_customer,
                            testId: `assessment-action-open-dashboard-${a.id}`,
                            onClick: () =>
                              navigate(`/transformation/dashboard?assessment_id=${a.id}`),
                          },
                          {
                            id: "restore",
                            label: restoringId === a.id ? "Restoring…" : "Restore",
                            icon: ArchiveRestore,
                            visible: isArchived,
                            disabled: restoringId === a.id,
                            testId: `assessment-action-restore-${a.id}`,
                            onClick: () => handleRestore(a),
                          },
                          {
                            id: "archive",
                            label: "Archive",
                            icon: ArchiveIcon,
                            visible: !isArchived,
                            testId: `assessment-action-archive-${a.id}`,
                            onClick: () =>
                              setPendingArchive({ id: a.id, company_name: a.company_name }),
                          },
                          {
                            id: "delete",
                            label: "Delete",
                            icon: Trash2,
                            destructive: true,
                            separatorBefore: true,
                            visible: isAdmin,
                            testId: `assessment-action-delete-${a.id}`,
                            onClick: () =>
                              setConfirmDelete({ id: a.id, company_name: a.company_name }),
                          },
                        ]}
                      />
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Admin-only delete confirmation */}
      <Dialog open={!!confirmDelete} onOpenChange={(open) => !open && !deleting && setConfirmDelete(null)}>
        <DialogContent data-testid="assessment-delete-modal" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base font-bold text-[#111827]">
              Delete evaluation?
            </DialogTitle>
            <DialogDescription className="text-sm text-[#6B7280] leading-relaxed">
              Are you sure you want to delete this evaluation? This action cannot be undone.
              {confirmDelete?.company_name && (
                <>
                  <br />
                  <span className="block mt-2 text-[#111827] font-medium">
                    {confirmDelete.company_name}
                  </span>
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setConfirmDelete(null)}
              disabled={deleting}
              data-testid="assessment-delete-cancel"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleConfirmDelete}
              disabled={deleting}
              data-testid="assessment-delete-confirm"
              className="bg-[#B91C1C] hover:bg-[#991B1B] text-white"
            >
              {deleting ? "Deleting…" : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ArchiveDialog
        open={!!pendingArchive}
        entityLabel="evaluation"
        testIdPrefix="assessment-archive"
        onClose={() => setPendingArchive(null)}
        onConfirm={handleConfirmArchive}
      />
    </div>
  );
}
